import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GoogleMapsService, LocationDetails } from '../../services/google-maps.service';

@Component({
  selector: 'app-address-location',
  templateUrl: './address-location.component.html',
  styleUrls: ['./address-location.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressLocationComponent),
      multi: true
    }
  ]
})
export class AddressLocationComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {
  @Input() placeholder: string = 'Street address';
    @Input() label: string = 'Address Line 1';
  @Input() required: boolean = false;
  @Output() getLocationDetails = new EventEmitter<LocationDetails>();

  @ViewChild('addressInput', { static: false }) addressInput!: ElementRef<HTMLInputElement>;

  addressValue: string = '';
  autocomplete: any;
  isLoading: boolean = false;
    isGoogleMapsLoaded: boolean = false;

  private onChange = (value: string) => {};
  private onTouched = () => {};

  constructor(
      private googleMapsService: GoogleMapsService
  ) { }

  async ngOnInit() {
    // Load Google Maps
      await this.loadGoogleMaps();
  }

  async loadGoogleMaps() {
    try {
      await this.googleMapsService.loadGoogleMapsScript();
      this.isGoogleMapsLoaded = true;
    } catch (error) {
      console.error('Failed to load Google Maps:', error);
    }
  }

  ngAfterViewInit() {
    if (this.isGoogleMapsLoaded && this.addressInput) {
      this.initAutocomplete();
    } else {
      // Retry after a short delay if Google Maps is still loading
      setTimeout(() => {
          if (this.addressInput && (window as any).google && (window as any).google.maps && (window as any).google.maps.places) {
          this.initAutocomplete();
          } else if (this.addressInput) {
              // Retry again if still not loaded
              setTimeout(() => {
                  if (this.addressInput && (window as any).google && (window as any).google.maps && (window as any).google.maps.places) {
                      this.initAutocomplete();
                  }
              }, 1000);
        }
      }, 500);
    }
  }

  ngOnDestroy() {
    if (this.autocomplete && (window as any).google) {
      (window as any).google.maps.event.clearInstanceListeners(this.autocomplete);
    }
  }

  initAutocomplete() {
      if (!this.addressInput || !(window as any).google || !(window as any).google.maps || !(window as any).google.maps.places) {
      return;
    }

    try {
      this.autocomplete = this.googleMapsService.initAutocomplete(this.addressInput.nativeElement);

      // Listen for place selection
      this.autocomplete.addListener('place_changed', () => {
        const place = this.autocomplete.getPlace();

        if (!place.geometry) {
          console.warn('No details available for the selected place');
          return;
        }

        this.isLoading = true;
        const formattedAddress = place.formatted_address || this.addressValue;
        this.addressValue = formattedAddress;
        // Update the input element value
        if (this.addressInput && this.addressInput.nativeElement) {
          this.addressInput.nativeElement.value = formattedAddress;
        }
        this.onChange(formattedAddress);
        this.onTouched();

        // Get location details
        this.googleMapsService.getLocationDetails(place).then(
          (locationDetails: LocationDetails) => {
            this.isLoading = false;
            this.getLocationDetails.emit(locationDetails);
          },
          (error) => {
            this.isLoading = false;
            console.error('Error getting location details:', error);
          }
        );
      });
    } catch (error) {
      console.error('Error initializing autocomplete:', error);
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.addressValue = value || '';
    if (this.addressInput && this.addressInput.nativeElement) {
      this.addressInput.nativeElement.value = this.addressValue;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.addressInput) {
      this.addressInput.nativeElement.disabled = isDisabled;
    }
  }

  onInputChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.addressValue = value;
    this.onChange(value);
  }

  onBlur(): void {
      this.onTouched();
  }
}
